import React, { useState, useEffect } from 'react';
import { PlusCircle, Search, Edit2, Trash2, Upload, UserPlus } from 'lucide-react';
import { AddPlayersModal } from '../components/AddPlayersModal';

const Teams = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddPlayersModal, setShowAddPlayersModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [editingTeam, setEditingTeam] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    short_name: '',
    country: '',
    primary_color: '#004D98',
    secondary_color: '#A50044',
    logoUrl: '',
    logo_file: null,
    logo_preview: null
  });

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page,
        limit: 10,
        ...(search && { search })
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/teams?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch teams');

      const data = await response.json();
      setTeams(data.data);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Error fetching teams:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      alert('Only PNG and JPG images are allowed');
      return;
    }

    // Validate file size (500KB max)
    if (file.size > 500 * 1024) {
      alert('File too large. Maximum size is 500KB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        logo_file: file,
        logo_preview: reader.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const uploadLogo = async () => {
    if (!formData.logo_file || !formData.short_name) {
      return null;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const uploadFormData = new FormData();
      uploadFormData.append('logo', formData.logo_file);
      uploadFormData.append('short_name', formData.short_name);

      const response = await fetch(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/teams/upload-logo`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: uploadFormData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const data = await response.json();
      return data.logo_url;
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Upload failed: ${error.message}`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let logoUrl = formData.logoUrl;

      // Upload logo if file is selected
      if (formData.logo_file) {
        const uploadedUrl = await uploadLogo();
        if (uploadedUrl) {
          logoUrl = uploadedUrl;
        } else {
          return; // Upload failed, don't proceed
        }
      }

      const token = localStorage.getItem('token');
      const url = editingTeam 
        ? `${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/teams/${editingTeam.id}`
        : `${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/teams`;
      
      const payload = {
        name: formData.name,
        display_name: formData.display_name,
        short_name: formData.short_name,
        country: formData.country,
        primary_color: formData.primary_color,
        secondary_color: formData.secondary_color,
        logoUrl: logoUrl
      };
      
      const response = await fetch(url, {
        method: editingTeam ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        alert(error.error);
        return;
      }

      setShowModal(false);
      setEditingTeam(null);
      resetForm();
      fetchTeams();
    } catch (error) {
      console.error('Error saving team:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`}/api/teams/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        alert(error.error);
        return;
      }

      fetchTeams();
    } catch (error) {
      console.error('Error deleting team:', error);
    }
  };

  const openEditModal = (team) => {
    setEditingTeam(team);
    setFormData({
      name: team.name,
      display_name: team.display_name || '',
      short_name: team.short_name || '',
      country: team.country || '',
      primary_color: team.primary_color || '#004D98',
      secondary_color: team.secondary_color || '#A50044',
      logoUrl: team.logo_url || '',
      logo_file: null,
      logo_preview: team.logo_url || null
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingTeam(null);
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      short_name: '',
      country: '',
      primary_color: '#004D98',
      secondary_color: '#A50044',
      logoUrl: '',
      logo_file: null,
      logo_preview: null
    });
  };

  const openAddPlayersModal = (team) => {
    setSelectedTeam(team);
    setShowAddPlayersModal(true);
  };

  const handlePlayersAdded = (createdPlayers) => {
    console.log(`Added ${createdPlayers.length} players to ${selectedTeam.name}`);
    fetchTeams(); // Refresh to update player count
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Teams</h1>
        <button
          onClick={openCreateModal}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
        >
          <PlusCircle size={20} />
          Add Team
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search teams..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
      </div>

      {/* Teams Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Short Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Colors</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Players</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Games</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {teams.map((team) => (
              <tr key={team.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {team.logo_url ? (
                      <img src={team.logo_url} alt={team.name} className="w-10 h-10 object-contain mr-3" />
                    ) : (
                      <div 
                        className="w-10 h-10 rounded-full mr-3 flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: team.primary_color || '#004D98' }}
                      >
                        {team.short_name ? team.short_name.substring(0, 3).toUpperCase() : team.name.substring(0, 3).toUpperCase()}
                      </div>
                    )}
                    <span className="font-medium">{team.name}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-600">{team.short_name || '-'}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-600">{team.country || '-'}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex gap-1">
                    {team.primary_color && (
                      <div 
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: team.primary_color }}
                        title="Primary"
                      />
                    )}
                    {team.secondary_color && (
                      <div 
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: team.secondary_color }}
                        title="Secondary"
                      />
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{team._count.players}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {team._count.homeGames + team._count.awayGames}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex gap-2">
                    <button
                      onClick={() => openAddPlayersModal(team)}
                      className="text-green-600 hover:text-green-800"
                      title="Add Players"
                    >
                      <UserPlus size={18} />
                    </button>
                    <button
                      onClick={() => openEditModal(team)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit Team"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(team.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete Team"
                    >
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="px-4 py-2 border rounded-lg disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-4 py-2">
            Page {page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === pagination.totalPages}
            className="px-4 py-2 border rounded-lg disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {editingTeam ? 'Edit Team' : 'Create Team'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Official Name */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Official Name * <span className="text-xs text-gray-500">(full legal name)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="Real Zaragoza Club de Fútbol"
                    required
                  />
                </div>

                {/* Display Name */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Display Name <span className="text-xs text-gray-500">(common name for events/timeline)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.display_name}
                    onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="Real Zaragoza"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional. If empty, will use Official Name.
                  </p>
                </div>

                {/* Short Name / Code */}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Short Name / Code * <span className="text-xs text-gray-500">(for overlays)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.short_name}
                    onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="RZA or FCB"
                    pattern="[A-Za-z0-9-]+"
                    maxLength="10"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    3-4 letters recommended (e.g., FCB, RMA, RZA). Used in scoreboards and logo filenames.
                  </p>
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium mb-1">Country</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="Spain"
                  />
                </div>

                {/* Colors */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Primary Color</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="w-16 h-10 rounded border cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="flex-1 px-4 py-2 border rounded-lg font-mono text-sm"
                        pattern="#[0-9A-Fa-f]{6}"
                        placeholder="#004D98"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Secondary Color</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        className="w-16 h-10 rounded border cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        className="flex-1 px-4 py-2 border rounded-lg font-mono text-sm"
                        pattern="#[0-9A-Fa-f]{6}"
                        placeholder="#A50044"
                      />
                    </div>
                  </div>
                </div>

                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium mb-1">Team Logo</label>
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <input
                        type="file"
                        accept=".png,.jpg,.jpeg"
                        onChange={handleFileChange}
                        className="w-full px-4 py-2 border rounded-lg"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        PNG or JPG, max 500KB, recommended 64×64px minimum
                      </p>
                    </div>
                    {formData.logo_preview && (
                      <div className="flex-shrink-0">
                        <img 
                          src={formData.logo_preview} 
                          alt="Preview" 
                          className="w-20 h-20 object-contain border rounded p-1"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <Upload size={16} className="animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    editingTeam ? 'Update' : 'Create'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Players Modal */}
      {showAddPlayersModal && selectedTeam && (
        <AddPlayersModal
          teamId={selectedTeam.id}
          teamName={selectedTeam.name}
          isOpen={showAddPlayersModal}
          onClose={() => {
            setShowAddPlayersModal(false);
            setSelectedTeam(null);
          }}
          onSuccess={handlePlayersAdded}
        />
      )}
    </div>
  );
};

export default Teams;
